import {
  ABP_BOOTSTRAP_STORAGE,
  ABP_AUTH_TOKEN_STORAGE,
  ABP_CULTURE_COOKIE,
  ABP_TENANT_COOKIE,
  ABP_USER_ID_STORAGE,
  PROFILE_STAKEHOLDER_STORAGE_KEY,
  BUXUS_API_BASE_URL,
  APPLICATION_CONFIGURATION_URL,
  CURRENT_LOGIN_INFORMATIONS_URL,
} from './config'
import { getCookie, setCookie } from './cookies'
import { httpJson } from './httpClient'
import type { AbpBootstrapData, DiscoverLoginMethodResult } from './types'
import { discoverLoginMethod } from '../../services/accountService'
import { setBootLoadingLabel } from '../auth/bootLoadingLabel'
import {
  AUTH_STORAGE_PURGED_KEY,
  endOidcAndServerSession,
  oidcManager,
  PROFILE_SYNCED_ON_CALLBACK_KEY,
  purgeClientAuthStorage,
  syncAccessTokenFromOidcStore,
  syncProfileStakeholderFromRegistrationApi,
} from '../auth/oidc'
import { registerAnalyticsStakeholderType } from '../analytics/webAnalytics'
import { setWebErrorUser } from '../errors/webErrorReporting'
import {
  identifyUser,
  resetAnalyticsUser,
  setUserProfile,
} from '../analytics/analytics'
import {
  LOGIN_PROFILE_ERROR_STORAGE_KEY,
  REGISTRATION_PROFILE_LOGIN_ERROR,
} from '../auth/loginErrorMessage'
import { clearLoginStartOidcClaim } from '../analytics/loginAnalytics'
class AbpAdapter {
  private bootstrapData: AbpBootstrapData | null = null

  private authHeaders(): Record<string, string> {
    const headers: Record<string, string> = { Accept: 'application/json' }
    const token = localStorage.getItem(ABP_AUTH_TOKEN_STORAGE)
    if (token) headers.Authorization = `Bearer ${token}`
    if (BUXUS_API_BASE_URL.includes('.ngrok-free.app')) {
      headers['ngrok-skip-browser-warning'] = 'true'
    }
    return headers
  }

  private parseGrantedPolicies(raw: unknown): Record<string, boolean> {
    if (!raw || typeof raw !== 'object') return {}
    const auth = (raw as Record<string, unknown>).auth
    if (!auth || typeof auth !== 'object') return {}
    const policies = (auth as Record<string, unknown>).grantedPolicies
    if (!policies || typeof policies !== 'object') return {}
    const out: Record<string, boolean> = {}
    for (const [key, value] of Object.entries(policies as Record<string, unknown>)) {
      if (value === true) out[key] = true
    }
    return out
  }

  private async fetchCurrentLoginInformations(): Promise<string[]> {
    try {
      const res = await fetch(CURRENT_LOGIN_INFORMATIONS_URL, {
        method: 'GET',
        headers: this.authHeaders(),
        credentials: 'omit',
        redirect: 'manual',
      })
      if (res.type === 'opaqueredirect' || res.status === 0 || !res.ok) return []
      const json = (await res.json().catch(() => null)) as {
        user?: { roleNames?: string[] | null }
      } | null
      const roles = json?.user?.roleNames
      return Array.isArray(roles) ? roles.filter((r): r is string => typeof r === 'string') : []
    } catch {
      return []
    }
  }

  private markSessionContextLoaded(
    roleNames: string[],
    grantedPermissions: Record<string, boolean> = {},
    raw: unknown = null,
  ): void {
    if (!this.bootstrapData) return
    this.bootstrapData = {
      ...this.bootstrapData,
      roleNames,
      grantedPermissions,
      sessionContextLoaded: true,
      isAuthenticated: false,
      raw: raw ?? this.bootstrapData.raw,
    }
    localStorage.setItem(ABP_BOOTSTRAP_STORAGE, JSON.stringify(this.bootstrapData))
  }

  private async loadApplicationLocalization(cultureName: string): Promise<void> {
    const url = `${BUXUS_API_BASE_URL}/abp/application-localization?CultureName=${encodeURIComponent(
      cultureName,
    )}`

    type ResourceValue = { texts?: Record<string, string> }
    type ApplicationLocalizationResponse = { resources?: Record<string, ResourceValue> }

    const res = (await httpJson<unknown>(url)) as ApplicationLocalizationResponse
    const resources = res?.resources

    if (!resources || typeof resources !== 'object') return

    const localization: AbpBootstrapData['localization'] = {}
    for (const [resourceName, resourceValue] of Object.entries(
      resources as Record<string, ResourceValue>,
    )) {
      const texts = resourceValue.texts
      if (!texts || typeof texts !== 'object') continue
      localization[resourceName] = texts as Record<string, string>
    }

    if (!this.bootstrapData) return
    this.bootstrapData = {
      ...this.bootstrapData,
      defaultSourceName: this.bootstrapData.defaultSourceName ?? 'AbpUi',
      localization,
    }
    localStorage.setItem(ABP_BOOTSTRAP_STORAGE, JSON.stringify(this.bootstrapData))
  }

  async bootstrap(): Promise<AbpBootstrapData | null> {
    const cached = localStorage.getItem(ABP_BOOTSTRAP_STORAGE)
    if (cached) {
      try {
        const parsed = JSON.parse(cached) as AbpBootstrapData
        this.bootstrapData = {
          ...parsed,
          roleNames: parsed.roleNames ?? [],
          sessionContextLoaded: parsed.sessionContextLoaded ?? false,
        }
        return this.bootstrapData
      } catch {
        // fall through to cookie/default bootstrap
      }
    }

    const tenantId = getCookie(ABP_TENANT_COOKIE)
    const cultureName = getCookie(ABP_CULTURE_COOKIE) ?? 'en'
    const userId = localStorage.getItem(ABP_USER_ID_STORAGE) ?? null

    const parsed: AbpBootstrapData = {
      userId,
      userName: null,
      givenName: null,
      surname: null,
      email: null,
      tenantId,
      tenantName: null,
      isAuthenticated: false,
      cultureName,
      defaultSourceName: null,
      localization: {},
      grantedPermissions: {},
      roleNames: [],
      sessionContextLoaded: false,
      raw: null,
    }

    this.bootstrapData = parsed
    localStorage.setItem(ABP_BOOTSTRAP_STORAGE, JSON.stringify(parsed))

    void this.loadApplicationLocalization(cultureName).catch(() => {
      /* non-fatal */
    })

    return parsed
  }

  getBootstrap(): AbpBootstrapData | null {
    return this.bootstrapData
  }

  isAuthenticated(): boolean {
    const token = localStorage.getItem(ABP_AUTH_TOKEN_STORAGE)?.trim()
    const userId = localStorage.getItem(ABP_USER_ID_STORAGE)?.trim()
    return !!(token && userId)
  }

  private invalidateUnverifiedSession(): void {
    this.clearSession()
    this.markSessionContextLoaded([])
  }

  markAuthenticated(userId = 'buxus-user'): void {
    localStorage.setItem(ABP_USER_ID_STORAGE, userId)
  }

  clearSession(): void {
    void purgeClientAuthStorage()
    if (this.bootstrapData) {
      this.bootstrapData = {
        ...this.bootstrapData,
        userId: null,
        userName: null,
        givenName: null,
        surname: null,
        email: null,
        isAuthenticated: false,
        roleNames: [],
        grantedPermissions: {},
        sessionContextLoaded: false,
      }
    }
  }

  /**
   * OIDC bearer probe against ABP application-configuration.
   * Hydrates auth, granted policies, and role names for feature gating.
   */
  async probeSession(): Promise<boolean> {
    let skipOidcResync = false
    try {
      if (sessionStorage.getItem(AUTH_STORAGE_PURGED_KEY) === '1') {
        sessionStorage.removeItem(AUTH_STORAGE_PURGED_KEY)
        skipOidcResync = true
        await oidcManager.removeUser().catch(() => {
          /* ignore */
        })
      }
    } catch {
      /* ignore */
    }
    if (!skipOidcResync) {
      await syncAccessTokenFromOidcStore()
    }
    if (!localStorage.getItem(ABP_AUTH_TOKEN_STORAGE)?.trim()) {
      return false
    }

    try {
      const res = await fetch(APPLICATION_CONFIGURATION_URL, {
        method: 'GET',
        headers: this.authHeaders(),
        credentials: 'omit',
        redirect: 'manual',
      })

      if (res.type === 'opaqueredirect' || res.status === 0) {
        return false
      }
      if (!res.ok) {
        if (res.status === 401 || res.status === 403) {
          this.invalidateUnverifiedSession()
        }
        return false
      }

      const contentType = res.headers.get('content-type') ?? ''
      if (!contentType.includes('application/json')) {
        return false
      }

      type AppConfig = {
        currentUser?: {
          isAuthenticated?: boolean
          userName?: string
          name?: string
          surName?: string
          email?: string
          id?: string
        }
        currentTenant?: { id?: string; name?: string }
      }
      const json = (await res.json().catch(() => null)) as AppConfig | null
      const currentUser = json?.currentUser
      const grantedPermissions = this.parseGrantedPolicies(json)

      if (!currentUser?.isAuthenticated) {
        this.invalidateUnverifiedSession()
        return false
      }

      let profileSyncedOnCallback = false
      try {
        if (sessionStorage.getItem(PROFILE_SYNCED_ON_CALLBACK_KEY) === '1') {
          sessionStorage.removeItem(PROFILE_SYNCED_ON_CALLBACK_KEY)
          profileSyncedOnCallback = true
        }
      } catch {
        /* ignore */
      }

      const profileResult =
        profileSyncedOnCallback
          ? { ok: true as const }
          : await syncProfileStakeholderFromRegistrationApi()
      if (profileResult.ok) {
        registerAnalyticsStakeholderType(localStorage.getItem(PROFILE_STAKEHOLDER_STORAGE_KEY))
      }
      if (!profileResult.ok) {
        const oidcUser = await oidcManager.getUser().catch(() => null)
        try {
          sessionStorage.setItem(
            LOGIN_PROFILE_ERROR_STORAGE_KEY,
            profileResult.loginErrorMessage?.trim() || REGISTRATION_PROFILE_LOGIN_ERROR,
          )
        } catch {
          /* ignore */
        }
        await endOidcAndServerSession({
          accessToken: oidcUser?.access_token ?? localStorage.getItem(ABP_AUTH_TOKEN_STORAGE),
          idToken: oidcUser?.id_token ?? null,
        })
        this.markSessionContextLoaded([])
        return false
      }

      const roleNames = await this.fetchCurrentLoginInformations()
      const currentTenant = json?.currentTenant

      const stableId =
        (typeof currentUser.id === 'string' && currentUser.id.trim()) ||
        (typeof currentUser.userName === 'string' && currentUser.userName.trim()) ||
        (typeof currentUser.email === 'string' && currentUser.email.trim()) ||
        'abp-session'

      this.markAuthenticated(stableId)
      setWebErrorUser(
        stableId,
        typeof currentUser.email === 'string' ? currentUser.email : null,
      )
      identifyUser(stableId)
      const email =
        typeof currentUser.email === 'string' && currentUser.email.trim()
          ? currentUser.email.trim()
          : null
      const userName =
        typeof currentUser.userName === 'string' && currentUser.userName.trim()
          ? currentUser.userName.trim()
          : null
      if (email || userName) {
        setUserProfile({
          ...(email ? { $email: email } : {}),
          ...(userName ? { $name: userName } : {}),
        })
      }
      const cultureName = this.getCulture()
      const next: AbpBootstrapData = {
        userId: stableId,
        userName:
          (typeof currentUser.userName === 'string' && currentUser.userName.trim()) || null,
        givenName: (typeof currentUser.name === 'string' && currentUser.name.trim()) || null,
        surname: (typeof currentUser.surName === 'string' && currentUser.surName.trim()) || null,
        email: (typeof currentUser.email === 'string' && currentUser.email.trim()) || null,
        tenantId:
          (typeof currentTenant?.id === 'string' && currentTenant.id.trim()) ||
          getCookie(ABP_TENANT_COOKIE) ||
          null,
        tenantName:
          (typeof currentTenant?.name === 'string' && currentTenant.name.trim()) || null,
        isAuthenticated: true,
        cultureName,
        defaultSourceName: this.bootstrapData?.defaultSourceName ?? null,
        localization: this.bootstrapData?.localization ?? {},
        grantedPermissions,
        roleNames,
        sessionContextLoaded: true,
        raw: json,
      }
      this.bootstrapData = next
      localStorage.setItem(ABP_BOOTSTRAP_STORAGE, JSON.stringify(next))
      return true
    } catch {
      // Transient network/API errors during dev reload must not wipe tokens or downloads.
      return false
    }
  }

  async logout(): Promise<void> {
    setBootLoadingLabel('Logging out…')
    setWebErrorUser(null)
    resetAnalyticsUser()
    // Intentional logout is not a sign-in failure — drop any boot/callback error so
    // /login does not show "Couldn't sign in" after Log Out from register.
    try {
      sessionStorage.removeItem(LOGIN_PROFILE_ERROR_STORAGE_KEY)
    } catch {
      /* ignore */
    }
    clearLoginStartOidcClaim()
    const oidcUser = await oidcManager.getUser().catch(() => null)
    const accessToken =
      localStorage.getItem(ABP_AUTH_TOKEN_STORAGE)?.trim() || oidcUser?.access_token?.trim() || null

    await endOidcAndServerSession({
      accessToken,
      idToken: oidcUser?.id_token ?? null,
    })

    if (this.bootstrapData) {
      this.bootstrapData = {
        ...this.bootstrapData,
        userId: null,
        userName: null,
        givenName: null,
        surname: null,
        email: null,
        isAuthenticated: false,
        roleNames: [],
        grantedPermissions: {},
        sessionContextLoaded: false,
      }
    }
  }

  can(permission: string): boolean {
    const b = this.bootstrapData
    if (!b) return false
    return !!b.grantedPermissions[permission]
  }

  /**
   * ABP feature toggle from application-configuration.
   * Missing / not-yet-loaded values use `fallback` (default true) so new default-off
   * flags do not flash on before probeSession writes `raw`.
   */
  featureEnabled(name: string, fallback = true): boolean {
    const raw = this.bootstrapData?.raw
    if (!raw || typeof raw !== 'object') return fallback
    const features = (raw as { features?: { values?: Record<string, unknown> } }).features
    const value = features?.values?.[name]
    if (value == null || value === '') return fallback
    return String(value).toLowerCase() !== 'false'
  }

  l(key: string, sourceName?: string): string {
    const b = this.bootstrapData
    if (!b) return key
    const localization = b.localization ?? {}

    const directSource = sourceName ?? b.defaultSourceName
    if (directSource) {
      const v = localization[directSource]?.[key]
      if (v != null) return v
    }

    for (const texts of Object.values(localization)) {
      const v = texts?.[key]
      if (v != null) return v
    }

    return key
  }

  getCulture(): string {
    const cookie = getCookie(ABP_CULTURE_COOKIE)
    if (cookie) return cookie
    return this.bootstrapData?.cultureName ?? 'en'
  }

  async setCulture(cultureName: string): Promise<void> {
    setCookie(ABP_CULTURE_COOKIE, cultureName)
    if (this.bootstrapData) {
      this.bootstrapData = { ...this.bootstrapData, cultureName }
    }

    await this.loadApplicationLocalization(cultureName).catch(() => {
      /* non-fatal */
    })
  }

  async discoverLoginMethod(email: string): Promise<DiscoverLoginMethodResult> {
    return discoverLoginMethod(email)
  }
}

export const abpAdapter = new AbpAdapter()
